import { useState, useEffect, useRef } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { AlertTriangle, Plus, Search, MapPin, Sparkles, Layers, Camera, X, FileImage, File, Loader2, Paperclip, Download } from "lucide-react";
import { toast } from "sonner";

export const IncidentsPage = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    incident_type: "deviation",
    title: "",
    description: "",
    severity: "low",
    location: ""
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject && selectedProject !== "all") {
      fetchIncidents(selectedProject);
    } else if (projects.length > 0) {
      fetchAllIncidents();
    }
  }, [selectedProject, projects]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllIncidents = async () => {
    if (projects.length === 0) {
      setIncidents([]);
      return;
    }
    setLoading(true);
    try {
      const allIncidents = [];
      for (const project of projects) {
        const res = await fetch(`${API}/projects/${project.project_id}/incidents`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        });
        if (res.ok) {
          const data = await res.json();
          // Add project name to each incident for display
          allIncidents.push(...data.map(i => ({ ...i, project_name: project.name })));
        }
      }
      setIncidents(allIncidents);
    } catch (err) {
      console.error("Failed to fetch incidents:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncidents = async (projectId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/incidents`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        const project = projects.find(p => p.project_id === projectId);
        setIncidents(data.map(i => ({ ...i, project_name: project?.name })));
      }
    } catch (err) {
      console.error("Failed to fetch incidents:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject || selectedProject === "all") return;

    setUploading(true);
    try {
      // First create the incident
      const res = await fetch(`${API}/projects/${selectedProject}/incidents`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const incident = await res.json();
        
        // Then upload any files
        if (selectedFiles.length > 0) {
          for (const file of selectedFiles) {
            const fileFormData = new FormData();
            fileFormData.append("file", file);
            
            await fetch(`${API}/incidents/${incident.incident_id}/attachments`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              credentials: "include",
              body: fileFormData
            });
          }
        }
        
        toast.success(language === "sv" ? "Avvikelse rapporterad!" : "Incident reported!");
        setDialogOpen(false);
        setFormData({ incident_type: "deviation", title: "", description: "", severity: "low", location: "" });
        setSelectedFiles([]);
        setAiAnalysis(null);
        if (selectedProject === "all") {
          fetchAllIncidents();
        } else {
          fetchIncidents(selectedProject);
        }
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = [".jpg", ".jpeg", ".png", ".gif", ".pdf", ".doc", ".docx", ".heic", ".heif"];
    
    const validFiles = files.filter(file => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!validTypes.includes(ext)) {
        toast.error(`${file.name}: ${language === "sv" ? "Filtypen stöds inte" : "File type not supported"}`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: ${language === "sv" ? "Filen är för stor (max 10MB)" : "File too large (max 10MB)"}`);
        return false;
      }
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openIncidentDetail = (incident) => {
    setSelectedIncident(incident);
    setDetailDialogOpen(true);
  };

  const downloadPdf = async (incidentId) => {
    try {
      const response = await fetch(`${API}/incidents/${incidentId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `avvikelse_${incidentId}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(language === "sv" ? "PDF nedladdad!" : "PDF downloaded!");
      } else {
        toast.error(language === "sv" ? "Kunde inte ladda ner PDF" : "Failed to download PDF");
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const analyzeRisk = async () => {
    setLoadingAI(true);
    try {
      const context = `Plats: ${formData.location || 'Okänd'}\nArbetsmoment: ${formData.title}\nBeskrivning: ${formData.description}`;
      const res = await fetch(`${API}/ai/analyze-risk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ context })
      });
      if (res.ok) {
        const data = await res.json();
        setAiAnalysis(data.analysis);
      }
    } catch (err) {
      toast.error(language === "sv" ? "Kunde inte analysera risk" : "Failed to analyze risk");
    } finally {
      setLoadingAI(false);
    }
  };

  const incidentTypes = [
    { value: "deviation", label: language === "sv" ? "Avvikelse" : "Deviation" },
    { value: "near_miss", label: language === "sv" ? "Tillbud" : "Near Miss" },
    { value: "accident", label: language === "sv" ? "Olycka" : "Accident" },
    { value: "safety_round", label: language === "sv" ? "Skyddsrond" : "Safety Round" }
  ];

  const severityConfig = {
    low: { color: "bg-green-100 text-green-800", label: language === "sv" ? "Låg" : "Low" },
    medium: { color: "bg-amber-100 text-amber-800", label: language === "sv" ? "Medium" : "Medium" },
    high: { color: "bg-orange-100 text-orange-800", label: language === "sv" ? "Hög" : "High" },
    critical: { color: "bg-red-100 text-red-800", label: language === "sv" ? "Kritisk" : "Critical" }
  };

  const statusConfig = {
    reported: { color: "bg-blue-100 text-blue-800", label: language === "sv" ? "Rapporterad" : "Reported" },
    investigating: { color: "bg-amber-100 text-amber-800", label: language === "sv" ? "Utreds" : "Investigating" },
    action_required: { color: "bg-orange-100 text-orange-800", label: language === "sv" ? "Åtgärd krävs" : "Action Required" },
    resolved: { color: "bg-green-100 text-green-800", label: language === "sv" ? "Åtgärdad" : "Resolved" }
  };

  const filteredIncidents = incidents.filter(i =>
    i.title.toLowerCase().includes(search.toLowerCase()) ||
    i.description.toLowerCase().includes(search.toLowerCase())
  );

  const openCount = incidents.filter(i => i.status !== "resolved").length;

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="incidents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Avvikelser & Arbetsmiljö" : "Incidents & Safety"}
            </h1>
            <p className="text-slate-600 mt-1">
              {language === "sv" 
                ? "Rapportera och hantera avvikelser, tillbud och olyckor" 
                : "Report and manage deviations, near misses and accidents"}
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-touch bg-red-600 hover:bg-red-700" disabled={!selectedProject || selectedProject === "all"} data-testid="report-incident-btn">
                <AlertTriangle className="w-5 h-5 mr-2" />
                {language === "sv" ? "Rapportera" : "Report"}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                  {language === "sv" ? "Rapportera avvikelse" : "Report Incident"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Typ" : "Type"}</Label>
                    <Select value={formData.incident_type} onValueChange={(v) => setFormData({...formData, incident_type: v})}>
                      <SelectTrigger data-testid="incident-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {incidentTypes.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Allvarlighet" : "Severity"}</Label>
                    <Select value={formData.severity} onValueChange={(v) => setFormData({...formData, severity: v})}>
                      <SelectTrigger data-testid="incident-severity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(severityConfig).map(([key, config]) => (
                          <SelectItem key={key} value={key}>{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Titel" : "Title"} *</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder={language === "sv" ? "Kort beskrivning av händelsen" : "Brief description of the incident"}
                    required
                    data-testid="incident-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Plats" : "Location"}</Label>
                  <Input 
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder={language === "sv" ? "T.ex. Plan 3, Trapphus A" : "E.g. Floor 3, Stairwell A"}
                    data-testid="incident-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Beskrivning" : "Description"} *</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={language === "sv" ? "Detaljerad beskrivning av vad som hände..." : "Detailed description of what happened..."}
                    rows={4}
                    required
                    data-testid="incident-desc-input"
                  />
                </div>

                {formData.description && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full"
                    onClick={analyzeRisk}
                    disabled={loadingAI}
                    data-testid="ai-risk-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {loadingAI 
                      ? (language === "sv" ? "Analyserar..." : "Analyzing...") 
                      : (language === "sv" ? "AI Riskanalys" : "AI Risk Analysis")}
                  </Button>
                )}

                {aiAnalysis && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 whitespace-pre-line">{aiAnalysis}</p>
                  </div>
                )}

                {/* File Upload Section */}
                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Bilagor" : "Attachments"}</Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.heic,.heif"
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="add-files-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {language === "sv" ? "Lägg till foto/fil" : "Add photo/file"}
                  </Button>
                  
                  {selectedFiles.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                          {file.type.startsWith("image/") ? (
                            <FileImage className="w-4 h-4 text-slate-500" />
                          ) : (
                            <File className="w-4 h-4 text-slate-500" />
                          )}
                          <span className="flex-1 text-sm truncate">{file.name}</span>
                          <span className="text-xs text-slate-400">{formatFileSize(file.size)}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => removeFile(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">
                    {language === "sv" 
                      ? "Max 10MB per fil. JPG, PNG, PDF, DOC stöds." 
                      : "Max 10MB per file. JPG, PNG, PDF, DOC supported."}
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => {
                    setDialogOpen(false);
                    setSelectedFiles([]);
                    setAiAnalysis(null);
                  }}>
                    {language === "sv" ? "Avbryt" : "Cancel"}
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-red-600 hover:bg-red-700" 
                    disabled={uploading}
                    data-testid="incident-submit-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {language === "sv" ? "Laddar upp..." : "Uploading..."}
                      </>
                    ) : (
                      language === "sv" ? "Rapportera" : "Report"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary */}
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-900" style={{ fontFamily: 'Barlow Condensed' }}>
                  {openCount}
                </p>
                <p className="text-sm text-red-700">
                  {language === "sv" ? "Öppna avvikelser" : "Open incidents"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-full sm:w-[250px] h-12" data-testid="incident-project-select">
              <SelectValue placeholder={language === "sv" ? "Välj projekt" : "Select project"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  {language === "sv" ? "Alla projekt" : "All Projects"}
                </div>
              </SelectItem>
              {projects.map(p => (
                <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder={language === "sv" ? "Sök avvikelser..." : "Search incidents..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12"
              data-testid="incident-search-input"
            />
          </div>
        </div>

        {/* Incidents List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : projects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {language === "sv" ? "Inga projekt tillgängliga" : "No projects available"}
              </p>
            </CardContent>
          </Card>
        ) : filteredIncidents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {search 
                  ? (language === "sv" ? "Inga avvikelser hittades" : "No incidents found")
                  : (language === "sv" ? "Inga rapporterade avvikelser" : "No reported incidents")}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredIncidents.map((incident) => {
              const severity = severityConfig[incident.severity] || severityConfig.low;
              const status = statusConfig[incident.status] || statusConfig.reported;
              const hasAttachments = incident.attachments && incident.attachments.length > 0;
              
              return (
                <Card 
                  key={incident.incident_id} 
                  className="hover:shadow-md transition-shadow cursor-pointer" 
                  onClick={() => openIncidentDetail(incident)}
                  data-testid={`incident-card-${incident.incident_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                            {incident.title}
                          </h3>
                          <Badge className={severity.color}>{severity.label}</Badge>
                          <Badge className={status.color}>{status.label}</Badge>
                          {hasAttachments && (
                            <Badge variant="outline" className="text-slate-500">
                              <Paperclip className="w-3 h-3 mr-1" />
                              {incident.attachments.length}
                            </Badge>
                          )}
                        </div>
                        {(!selectedProject || selectedProject === "all") && incident.project_name && (
                          <p className="text-xs text-primary font-medium mt-1">{incident.project_name}</p>
                        )}
                        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{incident.description}</p>
                        {incident.location && (
                          <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {incident.location}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Incident Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                {selectedIncident?.title}
              </DialogTitle>
            </DialogHeader>
            {selectedIncident && (
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  <Badge className={severityConfig[selectedIncident.severity]?.color}>
                    {severityConfig[selectedIncident.severity]?.label}
                  </Badge>
                  <Badge className={statusConfig[selectedIncident.status]?.color}>
                    {statusConfig[selectedIncident.status]?.label}
                  </Badge>
                </div>
                
                {selectedIncident.location && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4" />
                    {selectedIncident.location}
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">{language === "sv" ? "Beskrivning" : "Description"}</h4>
                  <p className="text-sm text-slate-600">{selectedIncident.description}</p>
                </div>

                {selectedIncident.attachments && selectedIncident.attachments.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">{language === "sv" ? "Bilagor" : "Attachments"}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedIncident.attachments.map((att, index) => {
                        const isImage = [".jpg", ".jpeg", ".png", ".gif", ".heic", ".heif"].includes(att.file_type?.toLowerCase());
                        return (
                          <a
                            key={att.file_id || index}
                            href={`${API}/files/${att.file_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isImage ? (
                              <div className="aspect-square rounded overflow-hidden mb-1">
                                <img 
                                  src={`${API}/files/${att.file_id}`} 
                                  alt={att.original_name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="aspect-square flex items-center justify-center bg-slate-100 rounded mb-1">
                                <File className="w-8 h-8 text-slate-400" />
                              </div>
                            )}
                            <p className="text-xs text-slate-600 truncate">{att.original_name}</p>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="text-xs text-slate-500">
                  {language === "sv" ? "Rapporterad" : "Reported"}: {new Date(selectedIncident.created_at).toLocaleString(language === "sv" ? "sv-SE" : "en-US")}
                </div>

                {/* Download PDF button */}
                <div className="pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => downloadPdf(selectedIncident.incident_id)}
                    data-testid="download-incident-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {language === "sv" ? "Ladda ner som PDF" : "Download as PDF"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};
